/**
 * DevicePanels
 *
 * Agrupa en un solo archivo todos los paneles laterales relacionados
 * con dispositivos POS: visualización (read-only) y creación/edición.
 *
 * Uso en Dashboard:
 *   const devicePanels = useDevicePanels();
 *   ...
 *   devicePanels.openDetail(device)   // botón "Ver"
 *   devicePanels.openEdit(device)     // botón "Editar"
 *   devicePanels.openCreate()         // botón "Nuevo Dispositivo"
 *   ...
 *   <DevicePanels hook={devicePanels} allCompanies={allCompanies} allBranches={allBranches} onSave={loadData} />
 */

import { useState } from "react";
import { type Device } from "@/services/devicesService";
import { type Company } from "@/services/companiesService";
import { type Branch } from "@/services/branchesService";
import DeviceDetailModal from "@/components/DeviceDetailModal";
import DeviceFormSheet from "@/components/DeviceFormSheet";
import DeviceCreatedModal from "@/components/DeviceCreatedModal";

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useDevicePanels() {
  // Panel de detalle (solo lectura)
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [detailDevice, setDetailDevice] = useState<Device | null>(null);

  // Panel de formulario (crear / editar)
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formDevice, setFormDevice] = useState<Device | null>(null);

  const openDetail = (device: Device) => {
    setDetailDevice(device);
    setIsDetailOpen(true);
  };

  const openEdit = (device: Device) => {
    setFormDevice(device);
    setIsFormOpen(true);
  };

  const openCreate = () => {
    setFormDevice(null);
    setIsFormOpen(true);
  };

  return {
    // Estado interno — usado por <DevicePanels />
    _state: { isDetailOpen, detailDevice, isFormOpen, formDevice },
    _setters: { setIsDetailOpen, setIsFormOpen },
    // Handlers públicos — usados por Dashboard
    openDetail,
    openEdit,
    openCreate,
  };
}

// ─── Componente ──────────────────────────────────────────────────────────────

interface DevicePanelsProps {
  hook: ReturnType<typeof useDevicePanels>;
  /** Lista de empresas para el selector en el formulario y para resolver en el detalle */
  allCompanies: Company[];
  /** Lista de sucursales para el selector filtrado y para resolver en el detalle */
  allBranches: Branch[];
  /** Se llama tras crear / editar / cambiar estado para recargar datos */
  onSave: () => void;
}

export function DevicePanels({
  hook,
  allCompanies,
  allBranches,
  onSave,
}: DevicePanelsProps) {
  const { _state: s, _setters: set } = hook;

  // Estado del modal de credenciales post-creación
  const [isCredentialsOpen, setIsCredentialsOpen] = useState(false);
  const [createdDeviceName, setCreatedDeviceName] = useState("");
  const [createdUsername, setCreatedUsername] = useState("");
  const [createdPassword, setCreatedPassword] = useState("");
  const [createdCompanyId, setCreatedCompanyId] = useState("");
  const [createdBranchId, setCreatedBranchId] = useState("");
  const [createdBackendUrl, setCreatedBackendUrl] = useState("");
  const [createdDbPassword, setCreatedDbPassword] = useState("");
  const [createdSyncToken, setCreatedSyncToken] = useState("");

  const handleDeviceCreated = (
    deviceName: string,
    username: string,
    password: string,
    companyId: string,
    branchId: string,
    backendUrl: string,
    dbPassword: string,
    syncToken: string,
  ) => {
    setCreatedDeviceName(deviceName);
    setCreatedUsername(username);
    setCreatedPassword(password);
    setCreatedCompanyId(companyId);
    setCreatedBranchId(branchId);
    setCreatedBackendUrl(backendUrl);
    setCreatedDbPassword(dbPassword);
    setCreatedSyncToken(syncToken);
    setIsCredentialsOpen(true);
  };

  // Resolver company y branch del dispositivo activo en el detalle
  const detailCompany = s.detailDevice
    ? (allCompanies.find((c) => c.id === s.detailDevice!.company_id) ?? null)
    : null;

  const detailBranch = s.detailDevice
    ? (allBranches.find((b) => b.id === s.detailDevice!.branch_id) ?? null)
    : null;

  return (
    <>
      {/* Panel de visualización */}
      <DeviceDetailModal
        isOpen={s.isDetailOpen}
        onClose={() => set.setIsDetailOpen(false)}
        device={s.detailDevice}
        company={detailCompany}
        branch={detailBranch}
      />

      {/* Panel de creación / edición */}
      <DeviceFormSheet
        isOpen={s.isFormOpen}
        onClose={() => set.setIsFormOpen(false)}
        device={s.formDevice}
        allCompanies={allCompanies}
        allBranches={allBranches}
        onSave={onSave}
        onCreated={handleDeviceCreated}
      />

      {/* Modal de credenciales — aparece tras crear un dispositivo nuevo */}
      <DeviceCreatedModal
        isOpen={isCredentialsOpen}
        onClose={() => {
          setIsCredentialsOpen(false);
          setCreatedPassword("");
          setCreatedDbPassword("");
          setCreatedSyncToken("");
        }}
        deviceName={createdDeviceName}
        username={createdUsername}
        password={createdPassword}
        companyId={createdCompanyId}
        branchId={createdBranchId}
        backendUrl={createdBackendUrl}
        dbPassword={createdDbPassword}
        syncToken={createdSyncToken}
      />
    </>
  );
}
